import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { Button } from "@progress/kendo-react-buttons";
import { SvgIcon } from "@progress/kendo-react-common";
import { Dialog, Window } from "@progress/kendo-react-dialogs";
import { TextBox, Switch } from "@progress/kendo-react-inputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { useLocalization } from "@progress/kendo-react-intl";
import { Avatar, ExpansionPanel, ExpansionPanelContent } from "@progress/kendo-react-layout";
import { Fade } from "@progress/kendo-react-animation";
import { Notification, NotificationGroup } from "@progress/kendo-react-notification";
import { eyeIcon, eyeSlashIcon, globeIcon, homeIcon, mapMarkerTargetIcon, plusIcon, saveIcon, starIcon, trashIcon, uploadIcon } from "@progress/kendo-svg-icons";
import axios from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import { AccessToken } from "../../../config/AccessToken";
import { loadingPanel } from "../../LoadingPanel";
import { AzureConfigMapComponent } from "./AzureConfigMapComponent";

export const ConfigMaps = () => {
    const PENDING_CREATED_MAP_ID_KEY = "pendingCreatedMapId";
    const PENDING_CREATED_MAP_NAME_KEY = "pendingCreatedMapName";

    const localizationService = useLocalization();
    const SERVER_URL = process.env.REACT_APP_SERVER_URL;
    const [instance, accessTokenRequest] = AccessToken();

    const [mapsList, setMapsList] = useState<any[]>([]);
    const [listMapNames, setListMapNames] = useState<string[]>([]);
    const [mapSearchTerm, setMapSearchTerm] = useState("");
    const [mapName, setMapName] = useState<string>("");
    const [selectedMapId, setSelectedMapId] = useState<any>(null);
    const [isGeoreferenced, setIsGeoreferenced] = useState(false);
    const [isMainMap, setIsMainMap] = useState(false);
    const [altitude, setAltitude] = useState<number>(0);
    const [isGeographicView, setIsGeographicView] = useState(true);
    const [isAerial, setIsAerial] = useState(false);
    const [showCornerCoordinates, setShowCornerCoordinates] = useState(false);
    const [mapScale, setMapScale] = useState<number | null>(null);
    const [selectedMap, setSelectedMap] = useState<any>(null);
    const [selectedMapImage, setSelectedMapImage] = useState<string | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [mapToDelete, setMapToDelete] = useState<any>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [showAddMapDialog, setShowAddMapDialog] = useState(false);
    const [newMapName, setNewMapName] = useState("");
    const altitudeOptions = [4, 3, 2, 1, 0, -1, -2, -3, -4];
    const [loading, setLoading] = useState(false);
    const [detailsExpanded, setDetailsExpanded] = useState(true);
    const uploadInputRef = useRef<HTMLInputElement | null>(null);
    const previousSelectedMapIdRef = useRef<any>(null);
    const lastSelectedMapIdRef = useRef<any>(null);
    const selectionLockMapIdRef = useRef<any>(null);
    const selectionLockMapNameRef = useRef<string | null>(null);
    const hasInitializedMapSelectionRef = useRef(false);
    const mapListRequestVersionRef = useRef(0);

    const mapImageSrc = selectedMapImage
        ? (selectedMapImage.startsWith("data:") ? selectedMapImage : `data:image/jpeg;base64, ${selectedMapImage}`)
        : null;

    const hasOtherMainMap = mapsList.some((map: any) =>
        (map?.main == 1 || map?.main == "1") && map?.id !== selectedMapId
    );

    const hasSelectedMapId = selectedMapId !== null && selectedMapId !== undefined && selectedMapId !== "";
    const isMapSelected = hasSelectedMapId;
    const [cornerDraft, setCornerDraft] = useState<any>(null);

    useEffect(() => {
        if (!isGeoreferenced) {
            setIsAerial(false);
        }
    }, [isGeoreferenced]);

    useEffect(() => {
        if (!successMessage) {
            return;
        }

        const timeoutId = window.setTimeout(() => setSuccessMessage(null), 3000);
        return () => window.clearTimeout(timeoutId);
    }, [successMessage]);

    const toFixed8OrEmpty = (value: any) => {
        if (value === null || value === undefined) {
            return "";
        }

        const rawValue = typeof value === "string" ? value.trim() : String(value);
        if (rawValue === "") {
            return "";
        }

        const normalizedValue = rawValue.replace(",", ".");
        const numericValue = Number(normalizedValue);
        if (!Number.isFinite(numericValue)) {
            return "";
        }

        // Keep at most 8 decimals, while allowing shorter representations (e.g. 1, 1.2).
        return numericValue.toFixed(8).replace(/\.?0+$/, "");
    };

    const buildCornerDraft = (map: any) => ({
        topLeft: {
            longitude: toFixed8OrEmpty(map?.topLeft?.longitude),
            latitude: toFixed8OrEmpty(map?.topLeft?.latitude),
            x: toFixed8OrEmpty(map?.topLeft?.x),
            y: toFixed8OrEmpty(map?.topLeft?.y),
        },
        topRight: {
            longitude: toFixed8OrEmpty(map?.topRight?.longitude),
            latitude: toFixed8OrEmpty(map?.topRight?.latitude),
            x: toFixed8OrEmpty(map?.topRight?.x),
            y: toFixed8OrEmpty(map?.topRight?.y),
        },
        botLeft: {
            longitude: toFixed8OrEmpty(map?.botLeft?.longitude),
            latitude: toFixed8OrEmpty(map?.botLeft?.latitude),
            x: toFixed8OrEmpty(map?.botLeft?.x),
            y: toFixed8OrEmpty(map?.botLeft?.y),
        },
        botRight: {
            longitude: toFixed8OrEmpty(map?.botRight?.longitude),
            latitude: toFixed8OrEmpty(map?.botRight?.latitude),
            x: toFixed8OrEmpty(map?.botRight?.x),
            y: toFixed8OrEmpty(map?.botRight?.y),
        },
    });

    const handleToggleCornerDialog = (value: boolean) => {
        if (value) {
            setCornerDraft(buildCornerDraft(selectedMap));
            setShowCornerCoordinates(true);
        } else {
            setShowCornerCoordinates(false);
            setCornerDraft(null);
        }
    };

    const handleCornerDraftChange = (
        cornerKey: "topLeft" | "topRight" | "botLeft" | "botRight",
        axis: "x" | "y" | "longitude" | "latitude",
        value: string
    ) => {
        const nextValue = toFixed8OrEmpty(value);

        setCornerDraft((prev: any) => ({
            ...prev,
            [cornerKey]: {
                ...(prev?.[cornerKey] ?? {}),
                [axis]: nextValue,
            },
        }));
    };

    const handleMapBoundsChange = (updatedCorners: any) => {
        if (!isGeoreferenced || !hasSelectedMapId) {
            return;
        }

        setSelectedMap((prev: any) => {
            if (!prev) {
                return prev;
            }

            const updated = { ...prev, ...updatedCorners };

            setMapsList((mapsPrev) =>
                mapsPrev.map((map: any) =>
                    map?.id === selectedMapId ? { ...map, ...updatedCorners } : map
                )
            );

            if (showCornerCoordinates) {
                setCornerDraft(buildCornerDraft(updated));
            }

            return updated;
        });
    };

    const toNumberOrNull = (value: string) => (value === "" ? null : Number(value));

    const toMax8DecimalsNumberOrNull = (value: any): number | null => {
        if (value === null || value === undefined) {
            return null;
        }

        const rawValue = typeof value === "string" ? value.trim().replace(",", ".") : String(value);
        if (rawValue === "") {
            return null;
        }

        const numericValue = Number(rawValue);
        if (!Number.isFinite(numericValue)) {
            return null;
        }

        return Number(numericValue.toFixed(8));
    };

    const handleSaveCornerDraft = () => {
        if (!cornerDraft) {
            setShowCornerCoordinates(false);
            return;
        }

        const buildCorner = (corner: any) => ({
            longitude: toNumberOrNull(corner?.longitude ?? ""),
            latitude: toNumberOrNull(corner?.latitude ?? ""),
            x: toNumberOrNull(corner?.x ?? ""),
            y: toNumberOrNull(corner?.y ?? ""),
        });

        const updatedCorners = {
            topLeft: buildCorner(cornerDraft.topLeft),
            topRight: buildCorner(cornerDraft.topRight),
            botLeft: buildCorner(cornerDraft.botLeft),
            botRight: buildCorner(cornerDraft.botRight),
        };

        setSelectedMap((prev: any) => (prev ? { ...prev, ...updatedCorners } : prev));
        setMapsList((mapsPrev) =>
            mapsPrev.map((map: any) =>
                map?.id === selectedMapId ? { ...map, ...updatedCorners } : map
            )
        );

        setShowCornerCoordinates(false);
        setCornerDraft(null);
    };

    const handleCancelCornerDraft = () => {
        setShowCornerCoordinates(false);
        setCornerDraft(null);
    };

    const handleChangeMapName = (event: any) => {
        setMapName(event.target.value);
    };

    const handleSelectMap = (map: any) => {
        hasInitializedMapSelectionRef.current = true;
        if (map?.id != null) {
            lastSelectedMapIdRef.current = map.id;
            if (selectionLockMapIdRef.current != null && selectionLockMapIdRef.current == map.id) {
                selectionLockMapIdRef.current = null;
                selectionLockMapNameRef.current = null;
            }
            previousSelectedMapIdRef.current = null;
        }

        const detectedCoordUnit = map?.coordUnit;

        setSelectedMapId(map?.id ?? null);
        setMapName(map?.name ?? "");
        const georeferenced = map?.georeferenced == 1;
        setIsGeoreferenced(georeferenced);
        setIsGeographicView(georeferenced);
        setIsAerial(georeferenced);
        setIsMainMap(map?.main == 1 || map?.main == "1");
        setAltitude(Number(map?.altitude ?? 0));
        setMapScale(toMax8DecimalsNumberOrNull(detectedCoordUnit));
        setSelectedMapImage(map?.image ?? null);
        setSelectedMap(map ?? null);
        if (!georeferenced) {
            setShowCornerCoordinates(false);
            setCornerDraft(null);
        }
    };

    const resetMapForm = () => {
        setSelectedMapId(null);
        setMapName("");
        setIsGeoreferenced(false);
        setIsMainMap(false);
        setAltitude(0);
        setIsGeographicView(true);
        setIsAerial(false);
        setShowCornerCoordinates(false);
        setMapScale(null);
        setSelectedMap(null);
        setSelectedMapImage(null);
        setCornerDraft(null);
        if (uploadInputRef.current) {
            uploadInputRef.current.value = "";
        }
    };

    const isExistingMapSelected = hasSelectedMapId && !String(selectedMapId).startsWith("new-");

    const handleSaveMap = async () => {
        if (!hasSelectedMapId) {
            return;
        }

        const selectedMapNameAtSave = mapName;
        const previousMapIdsAtSave = new Set(
            mapsList
                .filter((map: any) => map?.id != null)
                .map((map: any) => String(map.id))
        );

        const toNumberSafe = (value: any) => {
            const parsed = Number(value);
            return Number.isFinite(parsed) ? parsed : 0;
        };

        const coordUnitValue =
            mapScale !== null && mapScale !== undefined
                ? toNumberSafe(mapScale)
                : toNumberSafe(selectedMap?.coordUnit);

        const buildCornerPayload = (corner: any) => ({
            latitude: toNumberSafe(corner?.latitude),
            longitude: toNumberSafe(corner?.longitude),
            elevation: toNumberSafe(corner?.elevation),
            orientation: toNumberSafe(corner?.orientation),
            color: corner?.color ?? "",
        });

        const imageValue = (() => {
            // Keep the map's current image when switching an existing static map to georeferenced.
            const image = selectedMapImage || selectedMap?.image;
            if (!image) {
                return "";
            }

            if (image.startsWith("data:")) {
                const parts = image.split(",");
                return parts.length > 1 ? parts[1] : "";
            }

            return image;
        })();

        const payload = {
            name: mapName,
            image: imageValue,
            botLeft: buildCornerPayload(selectedMap?.botLeft),
            topLeft: buildCornerPayload(selectedMap?.topLeft),
            botRight: buildCornerPayload(selectedMap?.botRight),
            topRight: buildCornerPayload(selectedMap?.topRight),
            altitude: toNumberSafe(altitude),
            main: isMainMap ? 1 : 0,
            georeferenced: isGeoreferenced ? 1 : 0,
            coordUnit: coordUnitValue,
        };

        const endpoint = isExistingMapSelected ? `${SERVER_URL}/map/${selectedMapId}` : `${SERVER_URL}/map`;

        setLoading(true);

        const commonOperations = async (accessToken: any) => {
            const headers = { Authorization: `Bearer ${accessToken}`, Language: localStorage.getItem("languageId") };
            try {
                if (isExistingMapSelected) {
                    await axios.put(endpoint, payload, { headers });
                    selectionLockMapIdRef.current = selectedMapId;
                    selectionLockMapNameRef.current = selectedMapNameAtSave?.trim() || null;
                    await getMapList(true, selectedMapId, selectedMapNameAtSave, false);
                } else {
                    const createResponse: any = await axios.post(endpoint, payload, { headers });
                    const createdMapId = createResponse?.data?.data?.id
                        ?? createResponse?.data?.id
                        ?? createResponse?.data?.data?.map?.id
                        ?? createResponse?.data?.mapId
                        ?? createResponse?.data?.data?.mapId
                        ?? null;

                    if (createdMapId != null) {
                        localStorage.setItem(PENDING_CREATED_MAP_ID_KEY, String(createdMapId));
                    } else {
                        localStorage.removeItem(PENDING_CREATED_MAP_ID_KEY);
                    }
                    localStorage.setItem(PENDING_CREATED_MAP_NAME_KEY, selectedMapNameAtSave?.trim() || "");

                    selectionLockMapIdRef.current = createdMapId;
                    selectionLockMapNameRef.current = selectedMapNameAtSave?.trim() || null;
                    resetMapForm();
                    await getMapList(false, createdMapId, selectedMapNameAtSave, true, previousMapIdsAtSave);
                }
            } catch (error: any) {
                if (error instanceof InteractionRequiredAuthError) {
                    instance.acquireTokenRedirect(accessTokenRequest);
                }
            } finally {
                setLoading(false);
            }
        };

        if (localStorage.getItem("accessToken") != null) {
            const accessToken = localStorage.getItem("accessToken");
            await commonOperations(accessToken);
        } else {
            await instance.initialize();
            try {
                const accessTokenResponse: any = await instance.acquireTokenSilent(accessTokenRequest);
                const accessToken = accessTokenResponse.accessToken;
                await commonOperations(accessToken);
            } catch (error: any) {
                if (error instanceof InteractionRequiredAuthError) {
                    instance.acquireTokenRedirect(accessTokenRequest);
                }
                setLoading(false);
            }
        }
    };

    const handleUploadImageClick = () => {
        uploadInputRef.current?.click();
    };

    const handleUploadImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            const result = typeof reader.result === "string" ? reader.result : null;
            if (result) {
                setSelectedMapImage(result);

                // When an image is uploaded the map must be shown as static preview.
                //setIsGeoreferenced(false);
                setIsGeographicView(false);

                setSelectedMap((prev: any) => {
                    if (!prev) {
                        return prev;
                    }
                    return {
                        ...prev,
                        image: result,
                        georeferenced: 0,
                    };
                });

                setMapsList((prev) =>
                    prev.map((map: any) =>
                        map?.id === selectedMapId
                            ? {
                                ...map,
                                image: result,
                                georeferenced: 0,
                            }
                            : map
                    )
                );
            }
        };
        reader.readAsDataURL(file);
    };

    useEffect(() => {
        getMapList(true);
    }, []);

    const getMapList = async (
        flag: boolean,
        selectedMapIdToKeep?: any,
        selectedMapNameToKeep?: string,
        preferNewestMapSelection: boolean = false,
        previousMapIds?: Set<string>
    ) => {
        const requestVersion = ++mapListRequestVersionRef.current;
        setLoading(true);
        const commonOperations = async (accessToken: any) => {
            try {
                const headers = { Authorization: `Bearer ${accessToken}`, Language: localStorage.getItem("languageId") };
                const res = await axios.get(`${SERVER_URL}/map/full/list`, { headers });
                const data = res?.data?.data ?? [];

                if (requestVersion !== mapListRequestVersionRef.current) {
                    return;
                }

                setMapsList(data);
                setListMapNames(data.map((map: any) => map.name));

                if (data.length > 0) {
                    let mapToSelect: any = null;
                    const pendingCreatedMapId = localStorage.getItem(PENDING_CREATED_MAP_ID_KEY);
                    const pendingCreatedMapName = localStorage.getItem(PENDING_CREATED_MAP_NAME_KEY)?.trim() || null;

                    if (pendingCreatedMapId) {
                        mapToSelect = data.find((map: any) => String(map?.id) === pendingCreatedMapId) ?? null;
                    }

                    if (!mapToSelect && pendingCreatedMapName) {
                        const pendingNameMatches = data.filter(
                            (map: any) => String(map?.name ?? "").trim() === pendingCreatedMapName
                        );
                        mapToSelect = pendingNameMatches[pendingNameMatches.length - 1] ?? null;
                    }

                    if (selectedMapIdToKeep != null) {
                        mapToSelect = data.find((map: any) => map?.id == selectedMapIdToKeep) ?? null;
                    }

                    if (!mapToSelect && previousMapIds && previousMapIds.size > 0) {
                        const newMaps = data.filter((map: any) => map?.id != null && !previousMapIds.has(String(map.id)));
                        mapToSelect = newMaps[newMaps.length - 1] ?? null;
                    }

                    if (!mapToSelect && selectedMapNameToKeep?.trim()) {
                        const normalizedName = selectedMapNameToKeep.trim();
                        const mapsWithSameName = data.filter(
                            (map: any) => String(map?.name ?? "").trim() === normalizedName
                        );

                        mapToSelect = mapsWithSameName[mapsWithSameName.length - 1] ?? null;
                    }

                    if (!mapToSelect && selectionLockMapNameRef.current?.trim()) {
                        const normalizedLockName = selectionLockMapNameRef.current.trim();
                        const mapsWithLockedName = data.filter(
                            (map: any) => String(map?.name ?? "").trim() === normalizedLockName
                        );

                        mapToSelect = mapsWithLockedName[mapsWithLockedName.length - 1] ?? null;
                    }

                    if (!mapToSelect && preferNewestMapSelection) {
                        const mapsWithId = data.filter((map: any) => map?.id != null);
                        mapToSelect = mapsWithId[mapsWithId.length - 1] ?? null;
                    }

                    const mapIdForSelection = preferNewestMapSelection
                        ? (mapToSelect?.id
                            ?? selectedMapIdToKeep
                            ?? selectionLockMapIdRef.current
                            ?? null)
                        : (mapToSelect?.id
                            ?? selectionLockMapIdRef.current
                            ?? previousSelectedMapIdRef.current
                            ?? lastSelectedMapIdRef.current
                            ?? selectedMapId);

                    if (!mapToSelect && mapIdForSelection != null) {
                        mapToSelect = data.find((map: any) => map?.id == mapIdForSelection) ?? null;
                    }

                    if (mapToSelect) {
                        handleSelectMap(mapToSelect);
                        if (
                            (pendingCreatedMapId && String(mapToSelect?.id) === pendingCreatedMapId)
                            || (pendingCreatedMapName && String(mapToSelect?.name ?? "").trim() === pendingCreatedMapName)
                        ) {
                            localStorage.removeItem(PENDING_CREATED_MAP_ID_KEY);
                            localStorage.removeItem(PENDING_CREATED_MAP_NAME_KEY);
                        }
                    } else if (flag && mapIdForSelection == null && !hasInitializedMapSelectionRef.current) {
                        handleSelectMap(data[0]);
                    }
                } else {
                    localStorage.removeItem(PENDING_CREATED_MAP_ID_KEY);
                    localStorage.removeItem(PENDING_CREATED_MAP_NAME_KEY);
                    resetMapForm();
                }
            } catch (error: any) {
                if (error instanceof InteractionRequiredAuthError) {
                    instance.acquireTokenRedirect(accessTokenRequest);
                }
            } finally {
                if (requestVersion === mapListRequestVersionRef.current) {
                    setLoading(false);
                }
            }
        };

        if (localStorage.getItem("accessToken") != null) {
            const accessToken = localStorage.getItem("accessToken");
            await commonOperations(accessToken);
        } else {
            await instance.initialize();
            try {
                const accessTokenResponse: any = await instance.acquireTokenSilent(accessTokenRequest);
                const accessToken = accessTokenResponse.accessToken;
                await commonOperations(accessToken);
            } catch (error: any) {
                if (error instanceof InteractionRequiredAuthError) {
                    instance.acquireTokenRedirect(accessTokenRequest);
                }
                if (requestVersion === mapListRequestVersionRef.current) {
                    setLoading(false);
                }
            }
        }
    };

    const removeMap = async (mapId: any): Promise<boolean> => {
        if (mapId == null) {
            return false;
        }

        const commonOperations = async (accessToken: any) => {
            const headers = { Authorization: `Bearer ${accessToken}`, Language: localStorage.getItem("languageId") };
            try {
                await axios.delete(`${SERVER_URL}/map/${mapId}`, { headers });
                const wasDeletingSelectedMap = selectedMapId == mapId;
                const updatedMaps = mapsList.filter((map: any) => map?.id !== mapId);

                setMapsList(updatedMaps);
                setListMapNames(updatedMaps.map((map: any) => map.name));

                if (updatedMaps.length === 0) {
                    resetMapForm();
                    return true;
                }

                if (wasDeletingSelectedMap) {
                    handleSelectMap(updatedMaps[0]);
                }
                return true;
            } catch (error: any) {
                if (error instanceof InteractionRequiredAuthError) {
                    instance.acquireTokenRedirect(accessTokenRequest);
                }
                return false;
            }
        };

        if (localStorage.getItem("accessToken") != null) {
            const accessToken = localStorage.getItem("accessToken");
            return await commonOperations(accessToken);
        } else {
            await instance.initialize();
            try {
                const accessTokenResponse: any = await instance.acquireTokenSilent(accessTokenRequest);
                return await commonOperations(accessTokenResponse.accessToken);
            } catch (error: any) {
                if (error instanceof InteractionRequiredAuthError) {
                    instance.acquireTokenRedirect(accessTokenRequest);
                }
                return false;
            }
        }
    };

    const requestDeleteMap = (map: any) => {
        setMapToDelete(map);
        setShowDeleteConfirm(true);
    };

    const confirmDeleteMap = async () => {
        if (!mapToDelete) {
            setShowDeleteConfirm(false);
            return;
        }
        const deletedMapName = mapToDelete.name;
        const wasDeleted = await removeMap(mapToDelete.id);
        if (wasDeleted) {
            const messageTemplate = localizationService.toLanguageString(
                "configuration.maps.deleted_success",
                "Mappa {name} cancellata"
            );
            setSuccessMessage(messageTemplate.replace("{name}", deletedMapName ?? ""));
        }
        setShowDeleteConfirm(false);
        setMapToDelete(null);
    };

    const cancelDeleteMap = () => {
        setShowDeleteConfirm(false);
        setMapToDelete(null);
    };

    const openAddMapDialog = () => {
        previousSelectedMapIdRef.current = lastSelectedMapIdRef.current ?? selectedMapId ?? null;
        selectionLockMapIdRef.current = previousSelectedMapIdRef.current;
        setNewMapName("");
        setShowAddMapDialog(true);
    };

    const cancelAddMap = () => {
        setShowAddMapDialog(false);
        setNewMapName("");
    };

    const confirmAddMap = () => {
        const trimmedName = newMapName.trim();
        if (!trimmedName) {
            return;
        }

        const tempMapId = `new-${Date.now()}`;
        const emptyMap = {
            id: tempMapId,
            name: trimmedName,
            georeferenced: 0,
            main: 0,
            altitude: 0,
            coordUnit: null,
            image: null,
            topLeft: { longitude: null, latitude: null, x: null, y: null },
            topRight: { longitude: null, latitude: null, x: null, y: null },
            botLeft: { longitude: null, latitude: null, x: null, y: null },
            botRight: { longitude: null, latitude: null, x: null, y: null },
        };

        setMapsList((prev) => [emptyMap, ...prev]);
        setListMapNames((prev) => [trimmedName, ...prev]);

        setSelectedMapId(tempMapId);
        setSelectedMap(emptyMap);
        setMapName(trimmedName);

        setIsGeoreferenced(false);
        setIsMainMap(false);
        setAltitude(0);
        setIsGeographicView(true);
        setIsAerial(false);
        setShowCornerCoordinates(false);
        setCornerDraft(null);
        setMapScale(null);
        setSelectedMapImage(null);

        setShowAddMapDialog(false);
        setNewMapName("");
    };

    const bolderStyles = `
        .config-map-root {
            align-items: stretch;
            padding: 10px;
        }

        .config-map-left-panel {
            border: 1px solid #d6e8ee;
            border-radius: 14px;
            box-shadow: 0 10px 24px rgba(10, 55, 75, 0.08);
        }

        .config-map-title {
            font-weight: 800;
            letter-spacing: 0.4px;
            text-shadow: 0 1px 0 rgba(255, 255, 255, 0.7);
        }

        .config-map-item {
            background: #ffffff;
            border: 1px solid #cfe3ea;
            border-radius: 12px;
            padding: 6px 8px;
            box-shadow: 0 3px 10px rgba(10, 55, 75, 0.06);
            transition: transform 120ms ease, box-shadow 180ms ease;
        }

        .config-map-item:hover {
            transform: translateY(-1px);
            box-shadow: 0 8px 18px rgba(10, 55, 75, 0.12);
        }

        .detailsMapImageContainer {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
            gap: 10px 14px;
            width: 100%;
            align-items: stretch;
            grid-column: 7 / -1;
        }

        .detailsPanelGrid {
            display: grid;
            grid-template-columns: repeat(12, minmax(0, 1fr));
            gap: 10px 14px;
            width: 100%;
            padding-top: 6px;
        }

        .detailsActionRow {
            grid-column: 1 / 7;
            display: flex;
            align-items: flex-start;
            gap: 8px;
            flex-wrap: wrap;
            width: 100%;
            justify-content: flex-start;
        }

        .detailsMapNameInput {
            flex: 1 1 220px;
            min-width: 160px;
            max-width: 360px;
        }

        .detailsActionMeta {
            display: flex;
            flex-direction: column;
            gap: 8px;
            flex: 1 1 320px;
            min-width: 240px;
        }

        .detailsActionNameRow {
            display: flex;
            align-items: center;
            gap: 8px;
            //flex-wrap: wrap;
            width: 100%;
        }

        .detailsActionButtons {
            display: flex;
            align-items: center;
            gap: 8px;
            flex: 0 0 auto;
            margin-left: auto;
        }

        .detailsMapImageColumn {
            display: flex;
            flex-direction: column;
            gap: 8px;
            min-width: 0;
            width: 100%;
        }

        .detailsMapImageControlGroup {
            display: flex;
            flex-direction: column;
            gap: 8px;
            width: 100%;
        }

        .detailsMapImageRow {
            display: flex;
            align-items: center;
            gap: 30px;
            flex-wrap: wrap;
            width: 100%;
        }

        .detailsMapImageSwitchItem {
            display: flex;
            align-items: center;
            gap: 6px;
            min-width: 0;
        }

        .detailsMapImageFieldRow .detailsMapImageInput {
            flex: 0 0 170px;
            width: 170px;
            max-width: 170px;
        }

        .detailsMapImageFieldLabel {
            flex: 0 0 96px;
            min-width: 96px;
        }

        .detailsMapImageFieldControl {
            align-self: center;
        }

        .detailsMapImageInput {
            flex: 1 1 130px;
            min-width: 110px;
            width: 100%;
        }

        @media (max-width: 960px) {
            .detailsPanelGrid {
                grid-template-columns: 1fr;
            }

            .detailsMapImageContainer {
                grid-template-columns: 1fr;
                grid-column: 1 / -1;
            }

            .detailsActionRow {
                grid-column: 1 / -1;
                justify-content: flex-start;
                flex-direction: column;
                align-items: flex-start;
            }

            .detailsMapNameInput {
                max-width: 100%;
            }

            .detailsActionMeta {
                width: 100%;
                min-width: 0;
            }

            .detailsActionButtons {
                width: auto;
                justify-content: flex-end;
            }

            .detailsMapImageFieldRow .detailsMapImageInput {
                flex: 1 1 130px;
                width: 100%;
                max-width: none;
            }

            .detailsMapImageFieldLabel {
                flex: 1 1 100%;
                min-width: 0;
            }

            .detailsMapImageSwitchesRow {
                gap: 8px;
            }

            .detailsMapImageSwitchItem {
                width: 100%;
                flex-wrap: wrap;
            }
        }
    `;

    const shouldShowAzureMap = isGeoreferenced && isAerial;
    const normalizedMapSearch = mapSearchTerm.trim().toLowerCase();
    const filteredMapsList = useMemo(() => {
        if (!normalizedMapSearch) {
            return mapsList;
        }

        return mapsList.filter((map: any) => {
            const mapId = String(map?.id ?? "").toLowerCase();
            const mapNameValue = String(map?.name ?? "").toLowerCase();
            return mapId.includes(normalizedMapSearch) || mapNameValue.includes(normalizedMapSearch);
        });
    }, [mapsList, normalizedMapSearch]);
    const isUnsavedMap = (map: any) => String(map?.id ?? "").startsWith("new-");

    return (
        <div className="flex-parent-element config-map-root" style={{ overflowX: "hidden" }}>
            <style>{bolderStyles}</style>
            {loading && loadingPanel}
            <NotificationGroup
                style={{
                    right: 0,
                    bottom: 0,
                    position: "absolute",
                    alignItems: "flex-start",
                    flexWrap: "wrap-reverse",
                    zIndex: 1200,
                }}
            >
                <Fade enter={true} exit={true}>
                    {successMessage && (
                        <Notification
                            type={{ style: "success", icon: true }}
                            closable={true}
                            onClose={() => setSuccessMessage(null)}
                        >
                            <span>{successMessage}</span>
                        </Notification>
                    )}
                </Fade>
            </NotificationGroup>
            {/* left panel */}
            <div
                className="config-map-left-panel"
                style={{
                    display: "flex",
                    flexDirection: "column",
                    padding: "10px",
                    background: "#F7F7F7",
                    flex: "1.5 1 0px",
                    overflowY: "hidden",
                }}
            >
                <div style={{ textAlign: "center", marginBottom: "10px", justifyContent: "space-between" }}>
                    <p
                        className="config-map-title"
                        style={{
                            display: "inline-flex",
                            textAlign: "center",
                            fontSize: "20px",
                            color: "#1A8893",
                        }}
                    >
                        {localizationService.toLanguageString("configuration.maps.title", "NOT_FOUND")} ({normalizedMapSearch ? filteredMapsList.length : mapsList.length})
                    </p>
                    <Button
                        title={localizationService.toLanguageString("configuration.maps.title", "NOT_FOUND")}
                        themeColor={"primary"}
                        className="plusParam"
                        onClick={openAddMapDialog}
                        style={{
                            marginRight: "10px",
                            marginTop: "0px",
                            borderRadius: "10px",
                            boxShadow: "0 3px 10px rgba(26,136,147,0.28)",
                        }}
                    >
                        <Avatar className="plusParam">
                            <SvgIcon icon={plusIcon} style={{ margin: "8px" }} />
                        </Avatar>
                    </Button>
                </div>

                <div style={{ padding: "0 8px 8px 8px" }}>
                    <TextBox
                        value={mapSearchTerm}
                        onChange={(event: any) => setMapSearchTerm(event.target.value ?? "")}
                        placeholder={`${localizationService.toLanguageString("search", "Search")}...`}
                        style={{ width: "100%" }}
                    />
                </div>

                <div
                    className="flex-left-child-element"
                    style={{
                        flex: 1,
                        minHeight: 0,
                        padding: "8px",
                        overflowY: "auto",
                        width: "100%",
                    }}
                >
                    {filteredMapsList.map((map: any, index: number) => (
                        <div
                            key={map?.id ?? `map-${index}-${map?.name ?? ""}`}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                marginBottom: "6px"
                            }}
                        >
                            <div
                                className="trash-icon"
                                style={{
                                    cursor: isUnsavedMap(map) ? "not-allowed" : "pointer",
                                    alignContent: "center",
                                    opacity: isUnsavedMap(map) ? 0.45 : 1,
                                    pointerEvents: isUnsavedMap(map) ? "none" : "auto",
                                }}
                                onClick={() => {
                                    if (!isUnsavedMap(map)) {
                                        requestDeleteMap(map);
                                    }
                                }}
                                aria-disabled={isUnsavedMap(map)}
                                title={localizationService.toLanguageString("configuration.maps.delete", "NOT_FOUND")}
                            >
                                <Avatar className="trash-icon">
                                    <SvgIcon icon={trashIcon} style={{ verticalAlign: "baseline", margin: "10px" }} />
                                </Avatar>
                            </div>
                            <div style={{ display: "inline-flex", alignItems: "center", flex: 1 }}>
                                <p
                                    className="config-map-item"
                                    style={{
                                        cursor: "pointer",
                                        margin: "0",
                                        marginLeft: "10px",
                                        marginBottom: "0px",
                                        padding: "10px",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        flex: 1,
                                        color: selectedMapId === map?.id ? "#ffffff" : "inherit",
                                        background: selectedMapId === map?.id ? "#198792" : "#ffffff"
                                    }}
                                    onClick={() => handleSelectMap(map)}
                                >
                                    {map?.georeferenced == 1 ? (
                                        <SvgIcon
                                            className="map-globe-icon"
                                            icon={globeIcon}
                                            style={{ marginRight: "6px" }}
                                        />
                                    ) : (
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="map-cartography-icon"
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinejoin="round"
                                            style={{ marginRight: "6px" }}
                                        >
                                            <path d="M3 6l6-2 6 2 6-2v14l-6 2-6-2-6 2V6z" />
                                            <path d="M9 4v14" />
                                            <path d="M15 6v14" />
                                        </svg>
                                    )}
                                    {map?.name ?? ""}
                                </p>
                            </div>
                        </div>
                    ))}
                    {filteredMapsList.length === 0 && (
                        <p style={{ textAlign: "center", color: "#5d7278", marginTop: "12px" }}>
                            {localizationService.toLanguageString("map.no_map", "No map available to show")}
                        </p>
                    )}
                </div>
            </div>

            {showAddMapDialog && (
                <Dialog
                    title={localizationService.toLanguageString("configuration.maps.add_map_title", "Aggiungi mappa")}
                    onClose={cancelAddMap}
                >
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px", minWidth: "360px" }}>
                        <TextBox
                            value={newMapName}
                            onChange={(event: any) => setNewMapName(event.target.value)}
                            placeholder={localizationService.toLanguageString("map.name", "NOT_FOUND")}
                        />
                        <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                            <Button themeColor={"primary"} onClick={confirmAddMap} disabled={!newMapName.trim()}>
                                {localizationService.toLanguageString("save", "NOT_FOUND")}
                            </Button>
                            <Button onClick={cancelAddMap}>
                                {localizationService.toLanguageString("configuration.maps.cancel", "Annulla")}
                            </Button>
                        </div>
                    </div>
                </Dialog>
            )}

            {showDeleteConfirm && (
                <Dialog
                    className="confirm-action-dialog"
                    title={localizationService.toLanguageString("please_confirm", "NOT_FOUND")}
                    onClose={cancelDeleteMap}
                >
                    <p className="dialog-message">
                        {localizationService.toLanguageString("configuration.maps.delete_confirm", "NOT_FOUND")}
                    </p>
                    <div className="dialog-actions">
                        <button
                            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
                            onClick={cancelDeleteMap}
                        >
                            {localizationService.toLanguageString("no", "NOT_FOUND")}
                        </button>
                        <button
                            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
                            onClick={confirmDeleteMap}
                        >
                            {localizationService.toLanguageString("yes", "NOT_FOUND")}
                        </button>
                    </div>
                </Dialog>
            )}

            {/* right panel */}
            <div
                className="flex-right-child-element"
                style={{
                    paddingTop: "0px",
                    paddingBottom: "1px",
                    paddingRight: "10px",
                    overflowY: "auto",
                }}
            >
                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: "20px" }}>

                    {/*details*/}
                    <ExpansionPanel
                        title={localizationService.toLanguageString("configuration.maps.details", "Dettagli")}
                        expanded={detailsExpanded}
                        onAction={() => setDetailsExpanded(!detailsExpanded)}
                        style={{
                            background: "#ffffff",
                            borderRadius: "10px",
                            border: "1px solid #cfe3ea",
                            flex: "1 1 420px",
                            maxWidth: "100%",
                        }}
                    >
                        {detailsExpanded && (
                            <ExpansionPanelContent>
                                <div className="detailsPanelGrid">

                                    <div className="detailsActionRow">
                                        <div className="detailsActionMeta">
                                            <div className="detailsActionNameRow">
                                                <p style={{ fontSize: "16px", color: "#1A8893", margin: "0" }}>
                                                    {localizationService.toLanguageString("map.name", "NOT_FOUND")}
                                                </p>
                                                <TextBox
                                                    className="detailsMapNameInput"
                                                    value={mapName}
                                                    onChange={handleChangeMapName}
                                                    onKeyDown={(event: any) => {
                                                        if (event.key === " " || event.code === "Space") {
                                                            event.stopPropagation();
                                                        }
                                                    }}
                                                    placeholder=""
                                                    disabled={!isMapSelected}
                                                />

                                                <div className="detailsActionButtons">
                                                    <Button
                                                        title={
                                                            isExistingMapSelected
                                                                ? localizationService.toLanguageString("configuration.maps.update", "Aggiorna")
                                                                : localizationService.toLanguageString("save", "NOT_FOUND")
                                                        }
                                                        themeColor={"primary"}
                                                        className="saveParam"
                                                        onClick={handleSaveMap}
                                                        disabled={!isMapSelected}
                                                    >
                                                        <Avatar className="saveParam">
                                                            <SvgIcon icon={saveIcon} style={{ margin: "8px" }} />
                                                        </Avatar>
                                                    </Button>
                                                    <Button
                                                        title={localizationService.toLanguageString(
                                                            "configuration.maps.upload_image",
                                                            "Carica immagine (bmp, jpg, gif, png)"
                                                        )}
                                                        themeColor={"primary"}
                                                        className="saveParam"
                                                        onClick={handleUploadImageClick}
                                                        disabled={!isMapSelected}
                                                    >
                                                        <Avatar className="saveParam">
                                                            <SvgIcon icon={uploadIcon} style={{ margin: "8px" }} />
                                                        </Avatar>
                                                    </Button>
                                                </div>
                                            </div>

                                        </div>

                                        <input
                                            ref={uploadInputRef}
                                            type="file"
                                            accept=".bmp,.jpg,.jpeg,.gif,.png"
                                            onChange={handleUploadImageChange}
                                            disabled={!isMapSelected}
                                            style={{ display: "none" }}
                                        />
                                    </div>

                                    <div className="detailsMapImageContainer">
                                        <div className="detailsMapImageColumn">
                                            <div className="detailsMapImageControlGroup">
                                                <div className="detailsMapImageRow detailsMapImageSwitchesRow">
                                                    <div className="detailsMapImageSwitchItem">
                                                        <SvgIcon icon={globeIcon} style={{ color: "#198792" }} />
                                                        <Switch
                                                            checked={isGeoreferenced}
                                                            onChange={(e: any) => {
                                                                setIsGeoreferenced(e.value)
                                                                setIsGeographicView(e.value)
                                                                setIsAerial(e.value)
                                                            }}
                                                            disabled={!isMapSelected}
                                                        />
                                                        <label>{localizationService.toLanguageString("configuration.maps.georeferenced", "Georeferenziata")}</label>
                                                    </div>

                                                    <div className="detailsMapImageSwitchItem">
                                                        <span className={`map-star-icon ${isMainMap ? "is-on" : "is-off"}`}>
                                                            <SvgIcon icon={starIcon} />
                                                        </span>
                                                        <span
                                                            title={
                                                                hasOtherMainMap && !isMainMap
                                                                    ? localizationService.toLanguageString(
                                                                        "configuration.maps.main_map_exists",
                                                                        "Esiste gia una mappa principale"
                                                                    )
                                                                    : undefined
                                                            }
                                                        >
                                                            <Switch
                                                                checked={isMainMap}
                                                                onChange={(e: any) => {
                                                                    if (e.value && hasOtherMainMap) {
                                                                        return;
                                                                    }
                                                                    setIsMainMap(e.value);
                                                                }}
                                                                disabled={!isMapSelected || (!isMainMap && hasOtherMainMap)}
                                                            />
                                                        </span>
                                                        <label>{localizationService.toLanguageString("configuration.maps.main_map", "Mappa principale")}</label>
                                                    </div>
                                                </div>

                                                <div className="detailsMapImageRow detailsMapImageFieldRow">
                                                    <label className="detailsMapImageFieldLabel">
                                                        {localizationService.toLanguageString("configuration.maps.altitude", "Altitudine")}
                                                    </label>
                                                    <DropDownList
                                                        className="detailsMapImageInput detailsMapImageFieldControl"
                                                        data={altitudeOptions}
                                                        value={altitude}
                                                        onChange={(e: any) => setAltitude(e.value)}
                                                        disabled={!isMapSelected}
                                                    />
                                                </div>

                                                {isGeoreferenced ? (
                                                    <div className="detailsMapImageRow">
                                                        <span style={{ width: "22px", display: "inline-flex", justifyContent: "center" }}>
                                                            {isGeographicView ? (
                                                                <span
                                                                    style={{
                                                                        display: "inline-flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        border: "1px solid #198792",
                                                                        borderRadius: "3px",
                                                                        padding: "2px",
                                                                    }}
                                                                >
                                                                    <SvgIcon icon={globeIcon} style={{ color: "#198792" }} />
                                                                </span>
                                                            ) : (
                                                                <SvgIcon icon={homeIcon} style={{ color: "#198792" }} />
                                                            )}
                                                        </span>
                                                        <Switch
                                                            checked={isGeographicView}
                                                            onChange={(e: any) => {
                                                                setIsGeographicView(e.value);
                                                                setIsAerial(e.value);
                                                            }}
                                                            disabled={!isMapSelected}
                                                        />
                                                        <label>
                                                            {isGeographicView
                                                                ? localizationService.toLanguageString("map.geo", "Foto Aeree")
                                                                : localizationService.toLanguageString("map.static", "Mappa")}
                                                        </label>
                                                    </div>
                                                ) : (
                                                    <div className="detailsMapImageRow detailsMapImageFieldRow rightFields">
                                                        <label
                                                            className="detailsMapImageFieldLabel"
                                                            title={localizationService.toLanguageString(
                                                                "configuration.maps.scale_tooltip",
                                                                "Valori di 1 unita nel sistema di coordinate della mappa in metri"
                                                            )}
                                                        >
                                                            {localizationService.toLanguageString("configuration.maps.scale_m_udc", "Scala m/udc")}
                                                        </label>
                                                        <TextBox
                                                            className="detailsMapImageInput detailsMapImageFieldControl"
                                                            type="number"
                                                            value={mapScale ?? ""}
                                                            onChange={(e: any) => setMapScale(toMax8DecimalsNumberOrNull(e.target.value))}
                                                            title={localizationService.toLanguageString(
                                                                "configuration.maps.scale_tooltip",
                                                                "Valori di 1 unita nel sistema di coordinate della mappa in metri"
                                                            )}
                                                            disabled={!isMapSelected}
                                                        />
                                                    </div>
                                                )}
                                            </div>

                                            <div className="detailsMapImageRow">
                                                <span style={{ width: "22px", display: "inline-flex", justifyContent: "center" }}>
                                                    <SvgIcon icon={showCornerCoordinates ? eyeIcon : eyeSlashIcon} style={{ color: "#198792" }} />
                                                </span>
                                                <Switch
                                                    checked={showCornerCoordinates}
                                                    onChange={(e: any) => handleToggleCornerDialog(e.value)}
                                                    disabled={!isMapSelected}
                                                />
                                                <label>
                                                    {localizationService.toLanguageString(
                                                        "configuration.maps.show_corner_coordinates",
                                                        "Mostra coordinate angoli"
                                                    )}
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    {showCornerCoordinates && (
                                        <Window
                                            title={localizationService.toLanguageString("configuration.maps.vertices", "Vertici")}
                                            onClose={handleCancelCornerDraft}
                                            draggable={true}
                                            resizable={false}
                                            initialWidth={465}
                                            initialHeight={450}
                                            style={{ border: "2px solid #198792" }}
                                        >
                                            <div style={{ display: "flex", flexDirection: "column", gap: "8px", minWidth: "380px" }}>
                                                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                                                    <p style={{ margin: "0", fontWeight: 600 }}>
                                                        {localizationService.toLanguageString("configuration.maps.top_left", "Alto sx")}
                                                    </p>
                                                    {isGeoreferenced ? (
                                                        <div style={{ display: "flex", gap: "8px" }}>
                                                            <label>{localizationService.toLanguageString("configuration.maps.lon", "Lon")}:</label>
                                                            <TextBox
                                                                type="number"
                                                                value={cornerDraft?.topLeft?.longitude ?? ""}
                                                                onChange={(e: any) => handleCornerDraftChange("topLeft", "longitude", e.target.value)}
                                                                disabled={!isMapSelected}
                                                                style={{ width: "175px" }}
                                                            />
                                                            <label>{localizationService.toLanguageString("configuration.maps.lat", "Lat")}:</label>
                                                            <TextBox
                                                                type="number"
                                                                value={cornerDraft?.topLeft?.latitude ?? ""}
                                                                onChange={(e: any) => handleCornerDraftChange("topLeft", "latitude", e.target.value)}
                                                                disabled={!isMapSelected}
                                                                style={{ width: "175px" }}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div style={{ display: "flex", gap: "8px" }}>
                                                            <label>{localizationService.toLanguageString("configuration.maps.x", "X")}:</label>
                                                            <TextBox
                                                                type="number"
                                                                value={cornerDraft?.topLeft?.longitude ?? ""}
                                                                onChange={(e: any) => handleCornerDraftChange("topLeft", "longitude", e.target.value)}
                                                                disabled={!isMapSelected}
                                                                style={{ width: "175px" }}
                                                            />
                                                            <label>{localizationService.toLanguageString("configuration.maps.y", "Y")}:</label>
                                                            <TextBox
                                                                type="number"
                                                                value={cornerDraft?.topLeft?.latitude ?? ""}
                                                                onChange={(e: any) => handleCornerDraftChange("topLeft", "latitude", e.target.value)}
                                                                disabled={!isMapSelected}
                                                                style={{ width: "175px" }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>

                                                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                                                    <p style={{ margin: "0", fontWeight: 600 }}>
                                                        {localizationService.toLanguageString("configuration.maps.top_right", "Alto dx")}
                                                    </p>
                                                    {isGeoreferenced ? (
                                                        <div style={{ display: "flex", gap: "8px" }}>
                                                            <label>{localizationService.toLanguageString("configuration.maps.lon", "Lon")}:</label>
                                                            <TextBox
                                                                type="number"
                                                                value={cornerDraft?.topRight?.longitude ?? ""}
                                                                onChange={(e: any) => handleCornerDraftChange("topRight", "longitude", e.target.value)}
                                                                disabled={!isMapSelected}
                                                                style={{ width: "175px" }}
                                                            />
                                                            <label>{localizationService.toLanguageString("configuration.maps.lat", "Lat")}:</label>
                                                            <TextBox
                                                                type="number"
                                                                value={cornerDraft?.topRight?.latitude ?? ""}
                                                                onChange={(e: any) => handleCornerDraftChange("topRight", "latitude", e.target.value)}
                                                                disabled={!isMapSelected}
                                                                style={{ width: "175px" }}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div style={{ display: "flex", gap: "8px" }}>
                                                            <label>{localizationService.toLanguageString("configuration.maps.x", "X")}:</label>
                                                            <TextBox
                                                                type="number"
                                                                value={cornerDraft?.topRight?.longitude ?? ""}
                                                                onChange={(e: any) => handleCornerDraftChange("topRight", "longitude", e.target.value)}
                                                                disabled={!isMapSelected}
                                                                style={{ width: "175px" }}
                                                            />
                                                            <label>{localizationService.toLanguageString("configuration.maps.y", "Y")}:</label>
                                                            <TextBox
                                                                type="number"
                                                                value={cornerDraft?.topRight?.latitude ?? ""}
                                                                onChange={(e: any) => handleCornerDraftChange("topRight", "latitude", e.target.value)}
                                                                disabled={!isMapSelected}
                                                                style={{ width: "175px" }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>

                                                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                                                    <p style={{ margin: "0", fontWeight: 600 }}>
                                                        {localizationService.toLanguageString("configuration.maps.bottom_left", "Basso sx")}
                                                    </p>
                                                    {isGeoreferenced ? (
                                                        <div style={{ display: "flex", gap: "8px" }}>
                                                            <label>{localizationService.toLanguageString("configuration.maps.lon", "Lon")}:</label>
                                                            <TextBox
                                                                type="number"
                                                                value={cornerDraft?.botLeft?.longitude ?? ""}
                                                                onChange={(e: any) => handleCornerDraftChange("botLeft", "longitude", e.target.value)}
                                                                disabled={!isMapSelected}
                                                                style={{ width: "175px" }}
                                                            />
                                                            <label>{localizationService.toLanguageString("configuration.maps.lat", "Lat")}:</label>
                                                            <TextBox
                                                                type="number"
                                                                value={cornerDraft?.botLeft?.latitude ?? ""}
                                                                onChange={(e: any) => handleCornerDraftChange("botLeft", "latitude", e.target.value)}
                                                                disabled={!isMapSelected}
                                                                style={{ width: "175px" }}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div style={{ display: "flex", gap: "8px" }}>
                                                            <label>{localizationService.toLanguageString("configuration.maps.x", "X")}:</label>
                                                            <TextBox
                                                                type="number"
                                                                value={cornerDraft?.botLeft?.longitude ?? ""}
                                                                onChange={(e: any) => handleCornerDraftChange("botLeft", "longitude", e.target.value)}
                                                                disabled={!isMapSelected}
                                                                style={{ width: "175px" }}
                                                            />
                                                            <label>{localizationService.toLanguageString("configuration.maps.y", "Y")}:</label>
                                                            <TextBox
                                                                type="number"
                                                                value={cornerDraft?.botLeft?.latitude ?? ""}
                                                                onChange={(e: any) => handleCornerDraftChange("botLeft", "latitude", e.target.value)}
                                                                disabled={!isMapSelected}
                                                                style={{ width: "175px" }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>

                                                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                                                    <p style={{ margin: "0", fontWeight: 600 }}>
                                                        {localizationService.toLanguageString("configuration.maps.bottom_right", "Basso dx")}
                                                    </p>
                                                    {isGeoreferenced ? (
                                                        <div style={{ display: "flex", gap: "8px" }}>
                                                            <label>{localizationService.toLanguageString("configuration.maps.lon", "Lon")}:</label>
                                                            <TextBox
                                                                type="number"
                                                                value={cornerDraft?.botRight?.longitude ?? ""}
                                                                onChange={(e: any) => handleCornerDraftChange("botRight", "longitude", e.target.value)}
                                                                disabled={!isMapSelected}
                                                                style={{ width: "175px" }}
                                                            />
                                                            <label>{localizationService.toLanguageString("configuration.maps.lat", "Lat")}:</label>
                                                            <TextBox
                                                                type="number"
                                                                value={cornerDraft?.botRight?.latitude ?? ""}
                                                                onChange={(e: any) => handleCornerDraftChange("botRight", "latitude", e.target.value)}
                                                                disabled={!isMapSelected}
                                                                style={{ width: "175px" }}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div style={{ display: "flex", gap: "8px" }}>
                                                            <label>{localizationService.toLanguageString("configuration.maps.x", "X")}:</label>
                                                            <TextBox
                                                                type="number"
                                                                value={cornerDraft?.botRight?.longitude ?? ""}
                                                                onChange={(e: any) => handleCornerDraftChange("botRight", "longitude", e.target.value)}
                                                                disabled={!isMapSelected}
                                                                style={{ width: "175px" }}
                                                            />
                                                            <label>{localizationService.toLanguageString("configuration.maps.y", "Y")}:</label>
                                                            <TextBox
                                                                type="number"
                                                                value={cornerDraft?.botRight?.latitude ?? ""}
                                                                onChange={(e: any) => handleCornerDraftChange("botRight", "latitude", e.target.value)}
                                                                disabled={!isMapSelected}
                                                                style={{ width: "175px" }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end", marginTop: "12px" }}>
                                                <Button onClick={handleCancelCornerDraft}>
                                                    {localizationService.toLanguageString("configuration.maps.cancel", "Annulla")}
                                                </Button>
                                                <Button themeColor={"primary"} onClick={handleSaveCornerDraft}>
                                                    {localizationService.toLanguageString("save", "NOT_FOUND")}
                                                </Button>
                                            </div>
                                        </Window>
                                    )}
                                </div>
                            </ExpansionPanelContent>
                        )}
                    </ExpansionPanel>
                </div>

                <div
                    style={{
                        flexBasis: "100%",
                        width: "100%",
                        marginTop: "20px",
                        height: "90%",
                        boxShadow: "0px 0px 10px #19838F",
                        borderRadius: "10px",
                        display: "flex",
                        alignItems: shouldShowAzureMap ? "stretch" : "center",
                        justifyContent: shouldShowAzureMap ? "stretch" : "center",
                        overflow: "hidden",
                    }}
                >
                    {shouldShowAzureMap ? (
                        <div style={{ width: "100%", height: "100%" }}>
                            <AzureConfigMapComponent
                                map={selectedMap}
                                allowWorldView={shouldShowAzureMap}
                                fitToMapBounds={shouldShowAzureMap}
                                onMapBoundsChange={shouldShowAzureMap ? handleMapBoundsChange : undefined}
                            />
                        </div>
                    ) : mapImageSrc ? (
                        <img
                            src={mapImageSrc}
                            alt={localizationService.toLanguageString("configuration.maps.map_preview", "Anteprima mappa")}
                            style={{ width: "100%", height: "100%", objectFit: "contain" }}
                        />
                    )
                        : (
                            <p style={{ margin: "0", color: "#1A8893" }}>
                                {/*{localizationService.toLanguageString("configuration.maps.select_map", "Seleziona una mappa")}*/}
                            </p>
                        )
                    }
                </div>
            </div>
        </div>
    );
};
